import React, { useEffect, useState } from 'react';
import { Receipt, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
} from '../ui/dialog';
import { fetchFormulaCostBreakdown } from '../../lib/api';

/**
 * CostReceiptModal — the "Binary Price" itemised receipt.
 *
 * Pulls the live cost-plus-25 breakdown from /api/formula/{slug}/cost-breakdown
 * (formula-driven SKUs only; for legacy SKUs this returns a 404 — we hide
 * the trigger). Displays a mono-spaced receipt with four lines:
 *   Base Ingredients · Miron Glass · Atmosic Sensor · Dharma Markup (25 %)
 *
 * Visual: kraft-receipt aesthetic on the powder-blue palette, no graphics —
 * just numbers, ledger style.
 */

// Hardware components are split out from the formula's overhead bucket.
// formula.py overhead = glass+cap ($4.20) + labour ($1.60) + logistics ($2.10)
// We reframe glass+cap as Miron Glass and labour as Atmosic Sensor (the smart
// cap PCB + level sensor) for the founder's preferred receipt taxonomy.
const reframeBreakdown = (b) => {
  const o = b.overhead || {};
  return {
    baseIngredientsUsd: b.raw_ingredients_cost_usd,
    mironGlassUsd:      o.glass_bottle_and_cap_usd ?? 0,
    atmosicSensorUsd:   o.labour_usd ?? 0,
    logisticsUsd:       o.logistics_usd_pass_through ?? 0,
    totalCostUsd:       b.total_cost_usd,
    markupUsd:          (b.retail_price_usd || 0) - (b.total_cost_usd || 0),
    retailUsd:          b.retail_price_usd,
    volumeMl:           b.volume_ml,
    markupPct:          b.markup_percent,
  };
};

const CostReceiptModal = ({ slug, productName, triggerLabel = 'Cost Breakdown Receipt', testIdSuffix = '' }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || data || loading) return;
    setLoading(true);
    fetchFormulaCostBreakdown(slug)
      .then((b) => setData(reframeBreakdown(b)))
      .catch(() => setError('No formula receipt published for this SKU yet.'))
      .finally(() => setLoading(false));
  }, [open, slug, data, loading]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid={`cost-receipt-trigger${testIdSuffix}`}
          className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-[#7C9CB1] hover:text-[#90B0BF] transition-colors border-b border-[#7C9CB1]/40 hover:border-[#90B0BF] pb-0.5"
          onClick={(e) => e.stopPropagation()}
        >
          <Receipt size={12} strokeWidth={1.5} />
          Receipt
        </button>
      </DialogTrigger>

      <DialogContent
        data-testid={`cost-receipt-dialog${testIdSuffix}`}
        className="bg-[#F4F8F9] border-[#7C9CB1] max-w-md p-0 rounded-none gap-0 shadow-[0_24px_60px_-12px_rgba(124,156,177,0.45)]"
      >
        <DialogTitle className="sr-only">
          {productName} · Cost Breakdown Receipt
        </DialogTitle>
        <header className="px-7 py-6 border-b border-dashed border-[#7C9CB1]/40 text-center">
          <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#90B0BF]">
            Shreesavaa · Atelier Receipt
          </p>
          <h3 className="font-serif text-2xl md:text-3xl text-[#7C9CB1] mt-1 leading-tight">
            {productName}
          </h3>
          {data && (
            <p className="mt-1 font-mono text-[10px] tracking-[0.22em] uppercase text-[#90B0BF]">
              Volume · {data.volumeMl} ml · Cost-Plus-{data.markupPct}%
            </p>
          )}
        </header>

        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin text-[#7C9CB1]" size={20} />
          </div>
        )}
        {error && (
          <p className="px-7 py-10 font-serif italic text-[#90B0BF] text-center">
            {error}
          </p>
        )}

        {data && !loading && (
          <div data-testid={`cost-receipt-body${testIdSuffix}`} className="px-7 pt-6 pb-7">
            {/* Itemised lines */}
            <ul className="space-y-3 font-mono text-[12px] text-[#7C9CB1]">
              <ReceiptLine
                label="Base Ingredients"
                hint="botanical oils + Kewra hydrosol"
                value={data.baseIngredientsUsd}
              />
              <ReceiptLine
                label="Miron Glass"
                hint="violet apothecary vessel + cap"
                value={data.mironGlassUsd}
              />
              <ReceiptLine
                label="Atmosic Sensor"
                hint="smart-cap level + chain signing"
                value={data.atmosicSensorUsd}
              />
              <ReceiptLine
                label="Logistics (pass-through)"
                hint="0% markup · we do not profit from shipping"
                value={data.logisticsUsd}
                muted
              />
            </ul>

            <div className="my-5 border-t border-dashed border-[#7C9CB1]/40" />

            {/* Cost subtotal */}
            <div className="flex items-baseline justify-between font-mono text-[12px] text-[#7C9CB1]">
              <span className="tracking-[0.18em] uppercase">Cost subtotal</span>
              <span>${data.totalCostUsd.toFixed(2)}</span>
            </div>

            {/* Dharma markup */}
            <div className="mt-2 flex items-baseline justify-between font-mono text-[12px] text-[#7C9CB1]">
              <span className="tracking-[0.18em] uppercase">
                Dharma markup ({data.markupPct}%)
              </span>
              <span>+ ${data.markupUsd.toFixed(2)}</span>
            </div>
            <p className="mt-2 font-serif italic text-[11px] text-[#90B0BF] leading-snug">
              A seed for the future. We record only the truth of the markup.
              Every cent is auditable on the Artha Integrity ledger.
            </p>

            <div className="my-5 border-t-2 border-double border-[#7C9CB1]" />

            {/* Final binary price */}
            <div
              data-testid={`cost-receipt-final${testIdSuffix}`}
              className="flex items-baseline justify-between"
            >
              <span className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#90B0BF]">
                Final Binary Price
              </span>
              <span className="font-serif text-3xl text-[#7C9CB1]">
                ${data.retailUsd.toFixed(2)}
              </span>
            </div>
            <p className="mt-3 font-mono text-[9px] tracking-[0.32em] uppercase text-[#90B0BF] text-center">
              Past or nothing · Time is binary
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ReceiptLine = ({ label, hint, value, muted }) => (
  <li className="flex items-baseline justify-between gap-4">
    <span className={muted ? 'text-[#7C9CB1]/65' : ''}>
      <span className="tracking-[0.12em]">{label}</span>
      {hint && (
        <span className="block font-serif italic normal-case text-[10px] tracking-normal text-[#90B0BF] mt-0.5">
          {hint}
        </span>
      )}
    </span>
    <span className={`whitespace-nowrap ${muted ? 'text-[#7C9CB1]/65' : ''}`}>
      ${(value || 0).toFixed(2)}
    </span>
  </li>
);

export default CostReceiptModal;
